import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { User as UserIcon, LogOut, FileText, Settings, ChevronDown } from 'lucide-react';
import {
  selectCurrentUser,
  selectIsAuthenticated,
  logoutUser,
} from '../../store/slices/authSlice';

/**
 * AccountMenu — sits in the upper-right of the navbar.
 *
 * Logged out:  "Sign in" (charcoal text) → /signin
 * Logged in :  Gold initial-circle avatar + first name + chevron → dropdown
 *              with My Quotes / Account Settings / Sign out.
 *
 * Design system anchors:
 *   - Avatar: gold (--color-gold #B8965A) on charcoal-on-gold contrast (6.7:1, AA).
 *   - Dropdown radius: --radius-md (4px) — sharp/architectural.
 *   - Item type: Jost 14px tracking +0.04em, weight 500.
 *   - Menu shadow: --shadow-2 (cart-dropdown tier).
 *   - No emoji, Lucide icons only.
 */
export default function AccountMenu() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isAuth = useSelector(selectIsAuthenticated);
  const user = useSelector(selectCurrentUser);

  const handleLogout = async () => {
    await dispatch(logoutUser());
    navigate('/');
  };

  if (!isAuth) {
    return (
      <Link
        to="/signin"
        className="hidden sm:flex items-center gap-1.5 font-sans text-xs tracking-widest uppercase text-charcoal hover:text-gold transition-colors px-2 py-2"
        aria-label="Sign in"
      >
        <UserIcon size={16} />
        <span>Sign in</span>
      </Link>
    );
  }

  const firstName = user?.name?.split(' ')[0] || 'Account';
  const initials = (user?.name || user?.email || 'C R')
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          className="flex items-center gap-2 px-2 py-1 text-charcoal hover:text-gold transition-colors group"
          aria-label="Open account menu"
        >
          {user?.avatar ? (
            <img
              src={user.avatar}
              alt=""
              className="w-7 h-7 rounded-full object-cover border border-cream-200"
            />
          ) : (
            <span
              className="w-7 h-7 rounded-full flex items-center justify-center bg-gold text-charcoal font-sans text-[11px] font-semibold tracking-wide"
              aria-hidden="true"
            >
              {initials}
            </span>
          )}
          <span className="hidden md:inline font-sans text-xs tracking-widest uppercase">
            {firstName}
          </span>
          <ChevronDown
            size={13}
            className="hidden md:inline text-warm-stone group-hover:text-gold transition-colors"
          />
        </button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={8}
          className="w-60 bg-white border border-gray-100 shadow-xl z-50 animate-fade-in"
          // shadow-2 tier per design system §4.4
          style={{ borderRadius: '4px' }}
        >
          {/* Header */}
          <div className="px-4 py-3 border-b border-gray-100">
            <p className="font-serif text-base text-charcoal leading-tight truncate">
              {user?.name || 'Account'}
            </p>
            <p className="font-sans text-xs text-warm-stone truncate mt-0.5">
              {user?.email}
            </p>
            {user?.provider === 'google' && (
              <p className="font-sans text-[10px] tracking-widest uppercase text-warm-stone mt-1.5">
                Signed in with Google
              </p>
            )}
          </div>

          <div className="py-1">
            <DropdownMenu.Item asChild>
              <Link
                to="/account"
                className="flex items-center gap-3 px-4 py-2.5 font-sans text-sm text-charcoal hover:bg-cream-50 hover:text-gold transition-colors cursor-pointer outline-none focus:bg-cream-50 focus:text-gold"
              >
                <UserIcon size={15} className="text-warm-stone" />
                My Account
              </Link>
            </DropdownMenu.Item>

            <DropdownMenu.Item asChild>
              <Link
                to="/account/quotes"
                className="flex items-center gap-3 px-4 py-2.5 font-sans text-sm text-charcoal hover:bg-cream-50 hover:text-gold transition-colors cursor-pointer outline-none focus:bg-cream-50 focus:text-gold"
              >
                <FileText size={15} className="text-warm-stone" />
                My Quote Requests
              </Link>
            </DropdownMenu.Item>

            <DropdownMenu.Item asChild>
              <Link
                to="/account/settings"
                className="flex items-center gap-3 px-4 py-2.5 font-sans text-sm text-charcoal hover:bg-cream-50 hover:text-gold transition-colors cursor-pointer outline-none focus:bg-cream-50 focus:text-gold"
              >
                <Settings size={15} className="text-warm-stone" />
                Settings
              </Link>
            </DropdownMenu.Item>
          </div>

          <DropdownMenu.Separator className="h-px bg-gray-100" />

          <div className="py-1">
            <DropdownMenu.Item
              onSelect={(e) => {
                e.preventDefault();
                handleLogout();
              }}
              className="flex items-center gap-3 px-4 py-2.5 font-sans text-sm text-charcoal hover:bg-cream-50 hover:text-gold transition-colors cursor-pointer outline-none focus:bg-cream-50 focus:text-gold"
            >
              <LogOut size={15} className="text-warm-stone" />
              Sign out
            </DropdownMenu.Item>
          </div>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
